<template>
  <div class="container py-4 profile-page">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">Profil</h2>
      <button
        v-if="!editMode"
        class="btn btn-outline-secondary"
        @click="editMode = true"
      >
        <i class="fas fa-edit me-2"></i>Edit Profile
      </button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <div v-else>
      <!-- Profile Display -->
      <div v-if="!editMode">
        <div class="row g-4 align-items-center">
          <div class="col-auto">
            <img
              :src="profilePhoto"
              class="rounded-circle profile-avatar"
              :alt="displayName"
              width="96"
              height="96"
            />
          </div>
          <div class="col">
            <h4 class="mb-1">
              {{ displayName }}
              <span v-if="isWriter && authorProfile.is_verified" class="badge bg-success ms-2">Terverifikasi</span>
              <span v-if="!isWriter && user.is_premium" class="badge bg-warning text-dark ms-2">Premium</span>
              <span v-if="!isWriter && user.badge" class="badge bg-info text-dark ms-2">{{ user.badge }}</span>
            </h4>
            <div class="text-muted mb-2">
              <span v-if="isWriter">@{{ user.username }}</span>
              <span v-else>@{{ user.username }}</span>
              <span class="mx-2">|</span>
              <span>{{ user.email }}</span>
            </div>
            <div>
              <span class="badge bg-light text-dark me-2">{{ roleLabel }}</span>
            </div>
          </div>
        </div>

        <div class="row mt-4">
          <!-- User Info -->
          <div v-if="!isWriter" class="col-12 col-md-8">
            <div class="card mb-3">
              <div class="card-body">
                <h5 class="card-title mb-3">Informasi Pengguna</h5>
                <div class="mb-2"><strong>Username:</strong> {{ user.username }}</div>
                <div class="mb-2"><strong>Email:</strong> {{ user.email }}</div>
                <div class="mb-2"><strong>Role:</strong> {{ roleLabel }}</div>
                <div class="mb-2" v-if="user.badge"><strong>Badge:</strong> {{ user.badge }}</div>
                <div class="mb-2"><strong>Status Premium:</strong> <span :class="user.is_premium ? 'text-success' : 'text-secondary'">{{ user.is_premium ? 'Ya' : 'Tidak' }}</span></div>
              </div>
            </div>
          </div>
          <!-- Author Info -->
          <div v-else class="col-12 col-md-8">
            <div class="card mb-3">
              <div class="card-body">
                <h5 class="card-title mb-3">Profil Penulis</h5>
                <div class="mb-2"><strong>Nama:</strong> {{ authorProfile.name }}</div>
                <div class="mb-2"><strong>Nomor Telepon:</strong> {{ authorProfile.phone }}</div>
                <div class="mb-2" v-if="authorProfile.bio"><strong>Bio:</strong> {{ authorProfile.bio }}</div>
                <div class="mb-2"><strong>Genre:</strong> <span v-for="g in authorProfile.genres" :key="g" class="badge bg-secondary me-1">{{ g }}</span></div>
                <div class="mb-2"><strong>Pengalaman:</strong> {{ experienceLabel(authorProfile.experience_level) }}</div>
                <div class="mb-2" v-if="authorProfile.published_works"><strong>Karya Diterbitkan:</strong> {{ authorProfile.published_works }}</div>
                <div class="mb-2" v-if="authorProfile.website"><strong>Website:</strong> <a :href="authorProfile.website" target="_blank">{{ authorProfile.website }}</a></div>
                <div class="mb-2" v-if="authorProfile.instagram"><strong>Instagram:</strong> <a :href="'https://instagram.com/' + authorProfile.instagram" target="_blank">@{{ authorProfile.instagram }}</a></div>
                <div class="mb-2" v-if="authorProfile.twitter"><strong>Twitter:</strong> <a :href="'https://twitter.com/' + authorProfile.twitter" target="_blank">@{{ authorProfile.twitter }}</a></div>
                <div class="mb-2"><strong>Status Verifikasi:</strong> <span :class="authorProfile.is_verified ? 'text-success' : 'text-secondary'">{{ authorProfile.is_verified ? 'Terverifikasi' : 'Belum' }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit Profile Form -->
      <div v-else>
        <ProfileEditForm
          :is-writer="isWriter"
          :edit-user="editUser"
          :edit-author="editAuthor"
          :all-genres="allGenres"
          :profile-photo="profilePhoto"
          :display-name="displayName"
          :saving="saving"
          @save="handleProfileFormSave"
          @cancel="cancelEdit"
          @photo-change="onPhotoChange"
        />
      </div>
    </div>
  </div>

</template>

<script>
import axios from 'axios';
import ProfileEditForm from '@/components/ProfileEditForm.vue';
import Swal from 'sweetalert2';
import { useAuthStore } from '@/stores/auth';
import { resolveAvatarUrl } from '@/utils/avatar';

export default {
  name: 'Profile',
  components: {
    ProfileEditForm
  },
  data() {
    return {
      loading: true,
      saving: false,
      editMode: false,
      user: {},
      authorProfile: {},
      editUser: {},
      editAuthor: {},
      photoFile: null,
      allGenres: [
        'Fiksi', 'Non-Fiksi', 'Puisi', 'Biografi', 'Anak-anak', 'Petualangan',
        'Fantasi', 'Romantis', 'Misteri', 'Horor', 'Pengembangan Diri', 'Sejarah'
      ]
    };
  },
  computed: {
    isWriter() {
      return this.user.role === 'writer';
    },
    displayName() {
      if (this.isWriter && this.authorProfile.name) return this.authorProfile.name;
      return this.user.username || '';
    },
    profilePhoto() {
      const raw = this.isWriter
        ? (this.authorProfile.photo || '')
        : (this.user.avatar || '');
      const url = resolveAvatarUrl(raw);
      return url || require('@/assets/placeholder.svg');
    },
    roleLabel() {
      if (this.user.role === 'writer') return 'Penulis';
      if (this.user.role === 'admin') return 'Admin';
      if (this.user.role === 'reader') return 'Pembaca';
      return this.user.role || '';
    }
  },
  methods: {
    handleProfileFormSave({ editUser, editAuthor }) {
      // Update local state with latest edits from child before saving
      this.editUser = { ...editUser };
      this.editAuthor = { ...editAuthor };
      this.saveProfile();
    },
    async fetchProfile() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const userRes = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/users/me/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.user = userRes.data;
        if (this.user.role === 'writer') {
          const authorRes = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/author-profiles/me/`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          this.authorProfile = authorRes.data;
        }
        // Sinkronkan avatar ke localStorage supaya Navbar & Sidebar ikut ter-update
        this.syncAvatarToStorage();
      } catch (e) {
        // handle error (could show toast)
      }
      this.loading = false;
    },
    syncAvatarToStorage() {
      const raw = this.isWriter
        ? (this.authorProfile.photo || '')
        : (this.user.avatar || '');
      const avatarUrl = resolveAvatarUrl(raw);
      // Update store auth supaya Navbar & Sidebar ter-update langsung (Pinia reactivity)
      const authStore = useAuthStore();
      authStore.setProfile({
        username: this.user.username,
        role: this.user.role,
        avatar: avatarUrl,
      });
    },
    experienceLabel(level) {
      switch (level) {
        case 'beginner': return 'Pemula (0-1 tahun)';
        case 'intermediate': return 'Menengah (1-3 tahun)';
        case 'experienced': return 'Berpengalaman (3-5 tahun)';
        case 'professional': return 'Profesional (5+ tahun)';
        default: return '';
      }
    },
    onPhotoChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.photoFile = file;
        // Preview
        const reader = new FileReader();
        reader.onload = (ev) => {
          if (this.isWriter) this.authorProfile.photo = ev.target.result;
          else this.user.avatar = ev.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    enterEditMode() {
      this.editMode = true;
      this.editUser = { ...this.user };
      this.editAuthor = { ...this.authorProfile };
    },
    cancelEdit() {
      this.editMode = false;
      this.photoFile = null;
    },
    async saveProfile() {
      this.saving = true;
      try {
        const token = localStorage.getItem('token');
        if (!this.isWriter) {
          // User update
          const payload = {
            username: this.editUser.username,
            email: this.editUser.email,
            badge: this.editUser.badge,
            is_premium: this.editUser.is_premium
          };
          // Avatar upload (if changed)
          if (this.photoFile) {
            const formData = new FormData();
            formData.append('avatar', this.photoFile);
            await axios.patch(`${process.env.VUE_APP_API_BASE_URL}/api/users/me/`, formData, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
          }
          await axios.patch(`${process.env.VUE_APP_API_BASE_URL}/api/users/me/`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          // Author update
          const payload = {
            name: this.editAuthor.name,
            phone: this.editAuthor.phone,
            bio: this.editAuthor.bio,
            genres: this.editAuthor.genres,
            experience_level: this.editAuthor.experience_level,
            published_works: this.editAuthor.published_works,
            website: this.editAuthor.website,
            instagram: this.editAuthor.instagram,
            twitter: this.editAuthor.twitter
          };
          // Photo upload (if changed)
          if (this.photoFile) {
            const formData = new FormData();
            formData.append('photo', this.photoFile);
            await axios.patch(`${process.env.VUE_APP_API_BASE_URL}/api/author-profiles/me/`, formData, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
          }
          await axios.patch(`${process.env.VUE_APP_API_BASE_URL}/api/author-profiles/me/`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        await this.fetchProfile();
        this.editMode = false;
        this.photoFile = null;
        // Force reload avatar in case browser caches old image
        if (!this.isWriter && this.user.avatar) {
          this.user.avatar += (this.user.avatar.includes('?') ? '&' : '?') + 't=' + Date.now();
        }
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Profil berhasil diperbarui',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });
      } catch (e) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: 'Gagal memperbarui profil',
          text: 'Silakan coba lagi.',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true
        });
      }
      this.saving = false;
    }
  },
  mounted() {
    this.fetchProfile();
  },
  watch: {
    editMode(val) {
      if (val) {
        this.editUser = { ...this.user };
        this.editAuthor = { ...this.authorProfile };
      }
    }
  }
};
</script>

<style scoped>
.profile-page {
  max-width: 700px;
}
.profile-avatar {
  object-fit: cover;
  border: 2px solid #eaeaea;
  background: #fff;
}
.card {
  border-radius: 1rem;
}
@media (max-width: 600px) {
  .profile-page {
    padding: 0 0.5rem;
  }
  .profile-avatar {
    width: 72px !important;
    height: 72px !important;
  }
}
</style>